import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'introduction', title: 'Introducción', number: '1' },
    { id: 'owner-controller', title: 'Propietario y Controlador', number: '2' },
    { id: 'data-types', title: 'Tipos de Datos', number: '3' },
    { id: 'processing-mode', title: 'Procesamiento de Datos', number: '4' },
    { id: 'purposes', title: 'Propósitos', number: '5' },
    { id: 'google-api', title: 'Google API Services', number: '6' },
    { id: 'facebook-permissions', title: 'Permisos Facebook', number: '7' },
    { id: 'user-rights', title: 'Derechos de Usuarios', number: '8' },
    { id: 'additional-info', title: 'Información Adicional', number: '9' },
    { id: 'california-consumers', title: 'Consumidores California', number: '10' },
    { id: 'brazil-users', title: 'Usuarios Brasil', number: '11' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="privacy-policy">
      {/* Header Hero */}
      <motion.div 
        className="privacy-hero"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="privacy-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link to="/" className="back-link">
              <i className="fas fa-arrow-left"></i>
              Volver al inicio
            </Link>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Política de Privacidad
          </motion.h1>
          
          <motion.p
            className="privacy-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Última actualización: 28 de Junio de 2025
          </motion.p>

          <motion.div
            className="privacy-notice"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <i className="fas fa-info-circle"></i>
            <p>Esta aplicación recopila algunos Datos Personales de sus Usuarios. Este documento puede imprimirse para referencia.</p>
          </motion.div>
        </div>
      </motion.div>

      <div className="privacy-container">
        {/* Sidebar Navigation */}
        <motion.div 
          className="privacy-sidebar"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="sidebar-sticky">
            <h3>Contenidos</h3>
            <nav className="privacy-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="nav-item"
                >
                  <span className="nav-number">{section.number}</span>
                  <span>{section.title}</span>
                </button>
              ))}
            </nav>

            <div className="sidebar-contact">
              <h4>¿Necesitas ayuda?</h4>
              <p>Contáctanos para cualquier consulta sobre privacidad</p>
              <a href="mailto:hola@runamatic.io" className="contact-email">
                <i className="fas fa-envelope"></i>
                hola@runamatic.io
              </a>
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div 
          className="privacy-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {/* Section 1: Introduction */}
          <section id="introduction" className="privacy-section">
            <h2><span className="section-number">1.</span> Introducción</h2>
            <p>Esta aplicación recopila algunos Datos Personales de sus Usuarios.</p>
            <div className="highlight-box">
              <p><strong>Este documento contiene:</strong></p>
              <ul>
                <li>Una sección dedicada a los consumidores de California y sus derechos de privacidad.</li>
                <li>Una sección dedicada a los Usuarios brasileños y sus derechos de privacidad.</li>
              </ul>
            </div>
          </section>

          {/* Section 2: Owner and Data Controller */}
          <section id="owner-controller" className="privacy-section">
            <h2><span className="section-number">2.</span> Propietario y Controlador de Datos</h2>
            <div className="contact-info-box">
              <p><strong>Email de contacto del propietario:</strong> hola@runamatic.io</p>
            </div>
          </section>

          {/* Section 3: Types of Data collected */}
          <section id="data-types" className="privacy-section">
            <h2><span className="section-number">3.</span> Tipos de Datos Recopilados</h2>
            <p>Entre los tipos de Datos Personales que recopilamos se encuentran:</p>
            <div className="data-grid">
              <div className="data-item">Rastreador</div>
              <div className="data-item">Datos de uso</div>
              <div className="data-item">Email</div>
              <div className="data-item">Nombre</div>
              <div className="data-item">Apellido</div>
              <div className="data-item">Teléfono</div>
              <div className="data-item">Info de pago</div>
              <div className="data-item">Datos de servicio</div>
            </div>
            
            <div className="info-box">
              <h4>Información importante:</h4>
              <ul>
                <li>Los Datos Personales pueden ser proporcionados libremente por el Usuario o recopilados automáticamente.</li>
                <li>Todos los Datos solicitados por esta Aplicación son obligatorios salvo que se especifique lo contrario.</li>
                <li>Los Usuarios son responsables de cualquier Dato Personal de terceros compartido a través de esta Aplicación.</li>
                <li>Cualquier uso de Cookies sirve para proporcionar el Servicio requerido por el Usuario.</li>
              </ul>
            </div>
          </section>

          {/* Section 4: Mode and place of processing */}
          <section id="processing-mode" className="privacy-section">
            <h2><span className="section-number">4.</span> Modo y Lugar de Procesamiento</h2>
            
            <h3>Métodos de procesamiento</h3>
            <p>El Propietario toma medidas de seguridad apropiadas para prevenir acceso no autorizado, divulgación, modificación o destrucción de los Datos.</p>
            
            <h3>Base legal del procesamiento</h3>
            <div className="legal-bases">
              <div className="basis-item">
                <i className="fas fa-check-circle"></i>
                <span>Consentimiento del Usuario para propósitos específicos</span>
              </div>
              <div className="basis-item">
                <i className="fas fa-check-circle"></i>
                <span>Necesario para cumplimiento de contrato</span>
              </div>
              <div className="basis-item">
                <i className="fas fa-check-circle"></i>
                <span>Cumplimiento de obligación legal</span>
              </div>
              <div className="basis-item">
                <i className="fas fa-check-circle"></i>
                <span>Tarea de interés público</span>
              </div>
              <div className="basis-item">
                <i className="fas fa-check-circle"></i>
                <span>Intereses legítimos del Propietario</span>
              </div>
            </div>

            <h3>Lugar y Tiempo de retención</h3>
            <div className="important-notice">
              <p><strong>Lugar:</strong> Los Datos se procesan en las oficinas operativas del Propietario y lugares donde se ubican las partes involucradas.</p>
              <p><strong>Retención:</strong> Los Datos Personales serán procesados durante el tiempo requerido por el propósito para el que han sido recopilados. Una vez expire el período, los Datos serán eliminados.</p>
            </div>
          </section>

          {/* Section 5: Purposes of processing */}
          <section id="purposes" className="privacy-section">
            <h2><span className="section-number">5.</span> Propósitos del Procesamiento</h2>
            <p>Los Datos se recopilan para permitir al Propietario proporcionar su Servicio y para los siguientes propósitos:</p>
            
            <div className="purposes-grid">
              <div className="purpose-item"><i className="fas fa-chart-line"></i>Análisis</div>
              <div className="purpose-item"><i className="fas fa-envelope"></i>Contactar Usuario</div>
              <div className="purpose-item"><i className="fas fa-server"></i>Hosting</div>
              <div className="purpose-item"><i className="fas fa-credit-card"></i>Pagos</div>
              <div className="purpose-item"><i className="fas fa-user-plus"></i>Registro</div>
              <div className="purpose-item"><i className="fas fa-tags"></i>Gestión etiquetas</div>
              <div className="purpose-item"><i className="fas fa-comments"></i>Chat en vivo</div>
            </div>
          </section>

          {/* Continue with more sections... */}
          <section id="google-api" className="privacy-section">
            <h2><span className="section-number">6.</span> Google API Services</h2>
            <div className="compliance-box">
              <p><strong>Cumplimiento:</strong> Nuestra aplicación cumple con la Política de Datos de Usuario de Google API Services, incluyendo requisitos de Uso Limitado.</p>
            </div>
            
            <div className="google-services">
              <div className="service-item">
                <h4><i className="fab fa-google"></i> Google Sheets</h4>
                <p>Te permitimos obtener datos y actualizar tu Hoja de Cálculo.</p>
              </div>
              <div className="service-item">
                <h4><i className="fas fa-calendar"></i> Google Calendar</h4>
                <p>Leemos eventos para evitar sobrecargas y agregamos eventos cuando se reservan citas.</p>
              </div>
              <div className="service-item">
                <h4><i className="fas fa-robot"></i> Dialogflow</h4>
                <p>Automatizamos servicio al cliente detectando intenciones del usuario.</p>
              </div>
            </div>
          </section>

          <section id="facebook-permissions" className="privacy-section">
            <h2><span className="section-number">7.</span> Permisos de Facebook</h2>
            <p>Esta Aplicación puede solicitar permisos de Facebook para realizar acciones y recuperar información:</p>
            
            <div className="permissions-grid">
              <div className="permission-item">
                <h4>Información básica</h4>
                <p>ID, nombre, imagen, género y localización</p>
              </div>
              <div className="permission-item">
                <h4>API Gestión Negocios</h4>
                <p>Leer y escribir datos de negocio</p>
              </div>
              <div className="permission-item">
                <h4>Instagram básico</h4>
                <p>Información de perfil y medios</p>
              </div>
              <div className="permission-item">
                <h4>Mensajería de página</h4>
                <p>Enviar y recibir mensajes</p>
              </div>
              <div className="permission-item">
                <h4>Gestión publicaciones</h4>
                <p>Crear, editar y eliminar posts</p>
              </div>
              <div className="permission-item">
                <h4>Gestión anuncios</h4>
                <p>Administrar publicidad de la página</p>
              </div>
            </div>
          </section>

          <section id="user-rights" className="privacy-section">
            <h2><span className="section-number">8.</span> Derechos de los Usuarios</h2>
            <p>Los Usuarios pueden ejercer los siguientes derechos:</p>
            
            <div className="rights-grid">
              <div className="right-item">
                <i className="fas fa-hand-paper"></i>
                <h4>Retirar consentimiento</h4>
                <p>En cualquier momento donde se haya dado consentimiento</p>
              </div>
              <div className="right-item">
                <i className="fas fa-ban"></i>
                <h4>Objetar procesamiento</h4>
                <p>Si se realiza bajo base legal distinta al consentimiento</p>
              </div>
              <div className="right-item">
                <i className="fas fa-eye"></i>
                <h4>Acceder a Datos</h4>
                <p>Conocer si los Datos están siendo procesados</p>
              </div>
              <div className="right-item">
                <i className="fas fa-edit"></i>
                <h4>Rectificar Datos</h4>
                <p>Verificar precisión y solicitar actualizaciones</p>
              </div>
              <div className="right-item">
                <i className="fas fa-pause"></i>
                <h4>Restringir procesamiento</h4>
                <p>Bajo ciertas circunstancias específicas</p>
              </div>
              <div className="right-item">
                <i className="fas fa-trash"></i>
                <h4>Eliminar Datos</h4>
                <p>Obtener eliminación bajo ciertas circunstancias</p>
              </div>
            </div>

            <div className="exercise-rights-box">
              <h4>Cómo ejercer estos derechos</h4>
              <p>Las solicitudes pueden dirigirse al Propietario a través del contacto proporcionado. Son gratuitas y se atienden dentro de un mes.</p>
            </div>
          </section>

          <section id="additional-info" className="privacy-section">
            <h2><span className="section-number">9.</span> Información Adicional</h2>
            
            <div className="additional-topics">
              <div className="topic-item">
                <h4><i className="fas fa-gavel"></i> Acción legal</h4>
                <p>Los Datos pueden usarse para propósitos legales en tribunal o etapas previas a acciones legales.</p>
              </div>
              
              <div className="topic-item">
                <h4><i className="fas fa-cogs"></i> Logs del sistema</h4>
                <p>Para operación y mantenimiento se pueden recopilar archivos de interacción (System logs).</p>
              </div>
              
              <div className="topic-item">
                <h4><i className="fas fa-shield-alt"></i> Datos de terceros</h4>
                <p>No compartimos automáticamente datos sin consentimiento. El usuario tiene control total sobre sus datos.</p>
              </div>
              
              <div className="topic-item">
                <h4><i className="fas fa-user-slash"></i> "Do Not Track"</h4>
                <p>Esta Aplicación no admite solicitudes "Do Not Track".</p>
              </div>
              
              <div className="topic-item">
                <h4><i className="fas fa-sync-alt"></i> Cambios en política</h4>
                <p>Nos reservamos el derecho de realizar cambios notificando a los Usuarios en esta página.</p>
              </div>
            </div>
          </section>

          <section id="california-consumers" className="privacy-section">
            <h2><span className="section-number">10.</span> Consumidores de California</h2>
            <div className="california-notice">
              <p>Información específica según "The California Consumer Privacy Act of 2018" (CCPA).</p>
            </div>
            
            <h3>Categorías de información recopilada</h3>
            <div className="categories-list">
              <div className="category-item">Identificadores</div>
              <div className="category-item">Información comercial</div>
              <div className="category-item">Información de internet</div>
              <div className="category-item">Información inferida</div>
            </div>
            
            <h3>Derechos específicos</h3>
            <div className="california-rights">
              <div className="right-box">
                <h4>Derecho a saber</h4>
                <p>Categorías, fuentes y propósitos de información personal recopilada</p>
              </div>
              <div className="right-box">
                <h4>Derecho a eliminar</h4>
                <p>Solicitar eliminación de información personal (con excepciones legales)</p>
              </div>
              <div className="right-box">
                <h4>Derecho a opt-out</h4>
                <p>Oponerse a la venta de información personal</p>
              </div>
            </div>
          </section>

          <section id="brazil-users" className="privacy-section">
            <h2><span className="section-number">11.</span> Usuarios de Brasil</h2>
            <div className="brazil-notice">
              <p>Información específica según "Lei Geral de Proteção de Dados" (LGPD).</p>
            </div>
            
            <h3>Bases legales para procesamiento</h3>
            <div className="legal-bases-brazil">
              <div className="basis-item">Consentimiento a actividades de procesamiento</div>
              <div className="basis-item">Cumplimiento de obligación legal o regulatoria</div>
              <div className="basis-item">Realización de políticas públicas</div>
              <div className="basis-item">Estudios de entidades de investigación</div>
              <div className="basis-item">Realización de contrato</div>
              <div className="basis-item">Ejercicio de derechos en procedimientos judiciales</div>
              <div className="basis-item">Protección de vida o seguridad física</div>
              <div className="basis-item">Protección de la salud</div>
              <div className="basis-item">Intereses legítimos</div>
              <div className="basis-item">Protección crediticia</div>
            </div>
            
            <h3>Derechos específicos brasileños</h3>
            <div className="brazil-rights-grid">
              <div className="brazil-right">Confirmación de procesamiento</div>
              <div className="brazil-right">Acceso a información personal</div>
              <div className="brazil-right">Rectificación de datos</div>
              <div className="brazil-right">Anonimización o eliminación</div>
              <div className="brazil-right">Información sobre consentimiento</div>
              <div className="brazil-right">Información sobre terceros</div>
              <div className="brazil-right">Portabilidad de datos</div>
              <div className="brazil-right">Revocación de consentimiento</div>
              <div className="brazil-right">Queja ante ANPD</div>
            </div>
          </section>

          <div className="final-contact-section">
            <h3>Contacto para Consultas de Privacidad</h3>
            <p>Para cualquier consulta sobre esta política de privacidad o el manejo de datos personales:</p>
            <div className="contact-details">
              <a href="mailto:hola@runamatic.io" className="contact-email-final">
                <i className="fas fa-envelope"></i>
                hola@runamatic.io
              </a>
            </div>
            <p className="last-updated">Última actualización: 28 de Junio de 2025</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 